<h2 mat-dialog-title>
  <mat-icon>description</mat-icon>
  Policy Details
</h2>

<mat-dialog-content>
  <!-- Policy Header -->
  <div class="policy-header" *ngIf="data.policy">
    <div class="policy-name" *ngIf="data.policy.name">
      <strong>Name:</strong> {{ data.policy.name }}
    </div>
    <div class="policy-description" *ngIf="data.policy.description">
      <strong>Description:</strong> {{ data.policy.description }}
    </div>
    <div class="policy-id">
      <strong>Asset ID:</strong>
      <code>{{ data.assetId }}</code>
    </div>
  </div>

  <mat-divider></mat-divider>

  <!-- Policy Flags -->
  <div class="policy-flags" fxLayout="row" fxLayoutGap="16px">
    <div class="flag" *ngIf="data.policy?.permanent">
      <mat-icon color="warn">all_inclusive</mat-icon>
      <span>Permanent Policy</span>
    </div>
    <div class="flag" *ngIf="data.policy?.nextConditionHash">
      <mat-icon color="primary">link</mat-icon>
      <span>Has Chained Policy</span>
    </div>
  </div>

  <!-- Trusted Issuers -->
  <div class="section" *ngIf="data.policy?.trustedIssuers?.length">
    <h3>
      <mat-icon>verified_user</mat-icon>
      Trusted Issuers
    </h3>
    <mat-chip-listbox>
      <mat-chip *ngFor="let issuer of data.policy.trustedIssuers">
        {{ issuer }}
      </mat-chip>
    </mat-chip-listbox>
  </div>

  <!-- Group Composition -->
  <div class="section">
    <h3>
      <mat-icon>account_tree</mat-icon>
      Condition Structure
    </h3>
    <div class="composition-info">
      <strong>Group Logic:</strong>
      <span class="logic-badge" [ngClass]="data.policy?.groupComposition">
        {{ data.policy?.groupComposition }}
      </span>
      <span class="logic-description">
        ({{ data.policy?.groupComposition === 'ALL' ? 'All groups must pass' : 'At least one group must pass' }})
      </span>
    </div>
  </div>

  <!-- Condition Groups -->
  <div class="section groups-section">
    <h3>
      <mat-icon>rule</mat-icon>
      Condition Groups ({{ data.policy?.groups?.length || 0 }})
    </h3>

    <div class="no-groups" *ngIf="!data.policy?.groups?.length">
      No condition groups defined.
    </div>

    <mat-accordion *ngIf="data.policy?.groups?.length">
      <mat-expansion-panel *ngFor="let group of data.policy.groups; let i = index">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>folder</mat-icon>
            Group {{ i + 1 }}
            <span class="logic-badge small" [ngClass]="group.composition">{{ group.composition }}</span>
          </mat-panel-title>
          <mat-panel-description>
            {{ group.conditions?.length || 0 }} condition(s)
          </mat-panel-description>
        </mat-expansion-panel-header>

        <!-- Group Description -->
        <div class="group-description" *ngIf="group.description">
          <mat-icon>info</mat-icon>
          {{ group.description }}
        </div>

        <!-- Group Composition -->
        <div class="group-composition">
          <strong>Logic:</strong> {{ group.composition === 'ALL' ? 'All conditions must be met' : 'At least one condition must be met' }}
        </div>

        <!-- Conditions Table -->
        <table mat-table [dataSource]="group.conditions" class="conditions-table" *ngIf="group.conditions?.length">
          <ng-container matColumnDef="subject">
            <th mat-header-cell *matHeaderCellDef>Subject</th>
            <td mat-cell *matCellDef="let cond">
              <span class="subject-badge" [ngClass]="cond.subject.toLowerCase()">
                {{ cond.subject }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Attribute</th>
            <td mat-cell *matCellDef="let cond">{{ cond.name }}</td>
          </ng-container>

          <ng-container matColumnDef="predicate">
            <th mat-header-cell *matHeaderCellDef>Operator</th>
            <td mat-cell *matCellDef="let cond">
              <code>{{ getPredicateDisplay(cond.predicate) }}</code>
            </td>
          </ng-container>

          <ng-container matColumnDef="value">
            <th mat-header-cell *matHeaderCellDef>Value</th>
            <td mat-cell *matCellDef="let cond">
              <code>{{ formatValue(cond.value) }}</code>
            </td>
          </ng-container>

          <ng-container matColumnDef="issuers">
            <th mat-header-cell *matHeaderCellDef>Required Issuer(s)</th>
            <td mat-cell *matCellDef="let cond">
              <span *ngIf="cond.issuers?.length; else defaultIssuers">
                {{ cond.issuers.join(', ') }}
              </span>
              <ng-template #defaultIssuers>
                <span class="default-issuer">(policy default)</span>
              </ng-template>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="conditionColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: conditionColumns;"></tr>
        </table>

        <div class="no-conditions" *ngIf="!group.conditions?.length">
          No conditions in this group.
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>

  <!-- Chained Policy -->
  <div class="section" *ngIf="data.policy?.nextConditionHash">
    <h3>
      <mat-icon>arrow_forward</mat-icon>
      Next Policy in Chain
    </h3>
    <div class="chained-policy">
      <code>{{ data.policy.nextConditionHash }}</code>
      <button mat-icon-button (click)="copyToClipboard(data.policy.nextConditionHash)" matTooltip="Copy hash">
        <mat-icon>content_copy</mat-icon>
      </button>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="copyFullPolicy()" matTooltip="Copy full policy JSON">
    <mat-icon>content_copy</mat-icon>
    Copy JSON
  </button>
  <button mat-raised-button color="primary" mat-dialog-close>Close</button>
</mat-dialog-actions>
